import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SmoothLink from './SmoothLink';
import { homeImages } from '../utils/imageUtils';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close services dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.services-dropdown')) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Close services dropdown when route changes
  React.useEffect(() => {
    setIsServicesOpen(false);
  }, [location]);

  return (
    <nav className={`fixed w-full z-50 bg-white shadow-lg transition-all duration-300 ${isScrolled ? 'py-2' : 'py-3'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className={`flex-shrink-0 transition-all duration-300 ${isScrolled ? 'h-16' : 'h-20'}`}>
              <SmoothLink to="/" className="flex items-center">
                <img
                  src={homeImages.logo}
                  alt="EBuild & Construct"
                  className="h-full w-auto max-h-[108px] max-w-[108px] object-contain"
                />
                <span className={`ml-3 font-bold transition-all duration-300 ${isScrolled ? 'text-lg' : 'text-xl'} text-gray-800`}>
                  EBuild & Construct
                </span>
              </SmoothLink>
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
              aria-expanded="false"
            >
              <span className="sr-only">{isOpen ? 'Close menu' : 'Open menu'}</span>
              <svg
                className={`${isOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Desktop menu */}
          <div className="hidden sm:flex sm:space-x-8 sm:items-center">
            <SmoothLink 
              to="/" 
              className={`text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md font-medium transition-all duration-300 ${isScrolled ? 'text-base' : 'text-lg'}`}
            >
              Home
            </SmoothLink>
            <div className="relative services-dropdown">
              <button
                onClick={toggleServices}
                className={`text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md font-medium inline-flex items-center transition-all duration-300 ${isScrolled ? 'text-base' : 'text-lg'}`}
              >
                Services
                <svg
                  className={`ml-1 transition-all duration-300 ${isScrolled ? 'h-4 w-4' : 'h-5 w-5'}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isServicesOpen && (
                <div className="absolute z-10 -ml-4 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <SmoothLink
                      to="/new-builds"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      New Builds
                    </SmoothLink>
                    <SmoothLink
                      to="/renovations"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      Renovations
                    </SmoothLink>
                    <SmoothLink
                      to="/pools-and-landscapes"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                    >
                      Pools & Landscapes
                    </SmoothLink>
                  </div>
                </div>
              )}
            </div>
            <SmoothLink 
              to="/#contact" 
              className={`text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md font-medium transition-all duration-300 ${isScrolled ? 'text-base' : 'text-lg'} relative overflow-hidden`}
              style={{ 
                background: 'linear-gradient(135deg, #A6AA7A 60%, #C3C6A1 100%)', 
                border: '1px solid #A6AA7A',
                boxShadow: '0 2px 8px 0 rgba(166,170,122,0.08)'
              }}
              onMouseOver={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #8C8F63 60%, #A6AA7A 100%)')}
              onMouseOut={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #A6AA7A 60%, #C3C6A1 100%)')}
            >
              <span className="absolute inset-0 pointer-events-none rounded-md" style={{background: 'linear-gradient(120deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%)', opacity: 0.7}}></span>
              <span className="relative z-10">Contact</span>
            </SmoothLink>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <SmoothLink to="/" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
              Home
            </SmoothLink>
            <button
              onClick={toggleServices}
              className="w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            >
              Services
            </button>
            {isServicesOpen && (
              <div className="pl-4">
                <SmoothLink
                  to="/new-builds"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  New Builds
                </SmoothLink>
                <SmoothLink
                  to="/renovations"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  Renovations
                </SmoothLink>
                <SmoothLink
                  to="/pools-and-landscapes"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  Pools & Landscapes
                </SmoothLink>
              </div>
            )}
            <SmoothLink 
              to="/#contact" 
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-gray-900 rounded-md mt-1 relative overflow-hidden"
              style={{ 
                background: 'linear-gradient(135deg, #A6AA7A 60%, #C3C6A1 100%)', 
                border: '1px solid #A6AA7A',
                boxShadow: '0 2px 8px 0 rgba(166,170,122,0.08)'
              }}
              onMouseOver={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #8C8F63 60%, #A6AA7A 100%)')}
              onMouseOut={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #A6AA7A 60%, #C3C6A1 100%)')}
            >
              <span className="absolute inset-0 pointer-events-none rounded-md" style={{background: 'linear-gradient(120deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%)', opacity: 0.7}}></span>
              <span className="relative z-10">Contact</span>
            </SmoothLink>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation; 